import i18next, { TFunction } from 'i18next';
import { ERROR_KEYS } from '@repo/utils';
import { AxiosError, isAxiosError } from 'axios';
import { ErrorData } from '@/api/models';

export const NETWORK_ERROR_MESSAGE_KEY = 'errors.network';

const QUANTITY_ERROR_MESSAGES = new Map([
  [
    'errors.document_lines.invalid_quantity_reason.insufficient_stock',
    'errors.document_lines.stock_shortage',
  ],
  [
    'errors.document_lines.invalid_quantity_reason.insufficient_available',
    'errors.document_lines.stock_shortage',
  ],
  [
    'errors.document_lines.invalid_quantity_reason.non_positive',
    'errors.document_lines.quantity_must_be_positive',
  ],
]);

/**
 * Errori generici il cui motivo arriva in `params.reason` (codice chiuso del
 * backend): il testo umano vive nella sotto-chiave `<prefisso>.<reason>`.
 * Motivo assente o sconosciuto (backend più nuovo) → testo generico.
 */
const REASON_ERROR_MESSAGE_PREFIXES = new Map<string, string>([
  [
    ERROR_KEYS.ACCOUNT_MERGE.NOT_MERGEABLE,
    'errors.account_merge.not_mergeable_reason',
  ],
  [
    ERROR_KEYS.ACCOUNT_MERGE.PAIR_INVALID,
    'errors.account_merge.pair_invalid_reason',
  ],
]);

const REASON_CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

function reasonMessageKey(
  error: Pick<ErrorData, 'message' | 'params'>
): string | null {
  const prefix = REASON_ERROR_MESSAGE_PREFIXES.get(error.message);
  const reason = error.params?.reason;
  if (!prefix || !reason || !REASON_CODE_PATTERN.test(reason)) return null;
  const key = `${prefix}.${reason}`;
  return i18next.exists(key) ? key : null;
}

/**
 * Richiesta partita senza risposta (rete assente, server irraggiungibile):
 * l'interceptor del client la rilancia come `AxiosError` senza `response`,
 * mentre ogni risposta HTTP d'errore diventa `ErrorData`.
 */
export const isNetworkError = (error: unknown): error is AxiosError =>
  isAxiosError(error) && !error.response;

const isErrorDataShape = (error: unknown): error is ErrorData =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as Partial<ErrorData>).message === 'string' &&
  typeof (error as Partial<ErrorData>).statusCode === 'number';

/**
 * L'errore di una query o mutation come `ErrorData`, senza cast: l'interceptor
 * rilancia ogni risposta HTTP d'errore come `ErrorData`, una richiesta senza
 * risposta come `AxiosError` (qui il messaggio di rete) e il resto diventa un
 * `ErrorData` vuoto, che `getErrorMessage` traduce nel messaggio generico.
 */
export function toErrorData(error: unknown): ErrorData {
  if (error instanceof ErrorData) return error;
  if (isNetworkError(error)) {
    return new ErrorData({
      statusCode: 0,
      error: '',
      message: NETWORK_ERROR_MESSAGE_KEY,
    });
  }
  return new ErrorData(isErrorDataShape(error) ? error : null);
}

/**
 * Get translated error message from ApiError.
 * If the i18n key doesn't exist, returns a generic error message.
 *
 * @param error - The ApiError from axios/react-query
 * @param t - The i18next translation function
 * @returns Translated error message
 *
 * @example
 * // In a mutation hook:
 * onError: (error: ApiError) => {
 *   const errorMessage = getErrorMessage(error, t);
 *   toast.error(t('toast.title.failure'), {
 *     description: errorMessage
 *   });
 * }
 */
export function getErrorMessage(
  error: Pick<ErrorData, 'message' | 'params'>,
  t: TFunction
): string {
  if (isNetworkError(error)) return t(NETWORK_ERROR_MESSAGE_KEY) as string;

  if (error.message === 'errors.document_lines.invalid_quantity') {
    const messageKey = QUANTITY_ERROR_MESSAGES.get(
      error.params?.reasonKey ?? ''
    );
    if (messageKey) return t(messageKey) as string;
  }

  const reasonKey = reasonMessageKey(error);
  if (reasonKey) return t(reasonKey, error.params || {}) as string;

  // Check if the i18n key exists
  if (i18next.exists(error.message)) {
    // Translate with parameters if they exist
    return t(error.message, error.params || {}) as string;
  }

  // Fallback to generic error message
  return t('errors.generic') as string;
}

/**
 * Get error message with status code prefix (useful for debugging).
 *
 * @param error - The ApiError from axios/react-query
 * @param t - The i18next translation function
 * @returns Translated error message with status code
 *
 * @example
 * const message = getErrorMessageWithCode(error, t);
 * // Returns: "[404] User not found"
 */
export function getErrorMessageWithCode(
  error: ErrorData,
  t: TFunction
): string {
  const message = getErrorMessage(error, t);

  return `[${error.statusCode}] ${message}`;
}
